import {
    InsertDnsSearchDomainRequest,
    AddExtraDnsRecordRequest,
    AddRuleToRulesetRequest,
    InsertTenantSearchDomainRequest,
    CheckDeviceAccessRequest,
    Configuration,
    CreateAccessRuleRequest,
    CreateDeviceGroupRequest,
    CreateExtraDnsRecordsRequest,
    CreateLocalGroupRequest,
    CreateOidcProviderRequest,
    CreatePreAuthKeyRequest,
    CreateRulesetInstanceRequest,
    CreateRulesetRequest,
    CreateSplitDnsDomainRequest,
    CreateSplitDnsResolverRequest,
    CreateTagDirectoryRequest,
    CreateTagRequest,
    CreateTenantRequest,
    DefaultApi,
    EnableSubnetRequest,
    FullGlobalDns,
    GetAllDeviceGroupsRequest,
    GetAllExitNodesRequest,
    GetAllExtraDnsRequest,
    GetAllGroupsRequest,
    GetAllPluginsRequest,
    GetAllSubnetsRequest,
    GetAllTagsRequest,
    GetChangelogRequest,
    GetPendingExitNodesRequest,
    GetPendingRoutesRequest,
    GetRulesetsPageRequest,
    GetSingleGroupMembersRequest,
    GetUserNotInGroupRequest,
    InviteUserRequest,
    LocalAuthRequirements,
    MapResponse,
    MoveDnsSearchDomainRequest,
    MoveTagToAnotherDirectoryRequest,
    RecreateInvitationRequest,
    RenewUserAccessRequest,
    RequiredError,
    ResponseError,
    SetDeviceDnsnameRequest,
    SetEphemeralDeviceSettingsRequest,
    SetGeneralSettingsRequest,
    SetLdapConnectionSettingsRequest,
    SetLdapQuerySettingsRequest,
    SetOidcSettingsRequest,
    SetScimSettingsRequest,
    UpdateAccessRuleRequest,
    UpdateExtraDnsDomainRequest,
    UpdateExtraDnsRecordRequest,
    UpdateGroupRequest,
    UpdateOidcProviderRequest,
    UpdateRuleInRulesetRequest,
    UpdateRulesetRequest,
    UpdateSplitDnsDomainRequest,
    UpdateSplitDnsResolverRequest,
    UpdateTagDirectoryRequest,
    UpdateTenantRequest,
} from "src/api/generated/frontend_internal_v1";
import { ERROR_STORE } from "src/context/error-context";
import Console from "src/utils/console";
import { UUID } from "src/api/api_common";

const configuration = new Configuration({
    basePath: window.location.origin,
});

const defaultApi = new DefaultApi(configuration);

export const ApiInternal = {
    accounts: {
        getAll: () => handleError(defaultApi.getAllAccounts()),
        delete: (uuid: UUID) => handleError(defaultApi.deleteAccount({ uuid })),
    },
    settings: {
        getGeneral: () => handleError(defaultApi.getGeneralSettings()),
        setGeneral: (req: SetGeneralSettingsRequest) =>
            handleError(defaultApi.setGeneralSettings({ SetGeneralSettingsRequest: req })),
        getLocalAuthRequirements: () => handleError(defaultApi.getLocalAuthRequirements()),
        setLocalAuthRequirements: (requirements: LocalAuthRequirements) =>
            handleError(defaultApi.setLocalAuthRequirements({ SetLocalAuthRequirementsRequest: { requirements } })),
        getPortalAccessGroup: () => handleError(defaultApi.getPortalAccessGroup()),
        setPortalAccessGroup: (uuid: UUID) => handleError(defaultApi.setPortalAccessGroup({ SingleUuid: { uuid } })),
        getOidc: (tenant_uuid: UUID) => handleError(defaultApi.getOidcSettings({ tenant_uuid })),
        setOidc: (tenant_uuid: UUID, request: SetOidcSettingsRequest) =>
            handleError(defaultApi.setOidcSettings({ tenant_uuid, SetOidcSettingsRequest: request })),
        getScim: (tenant_uuid: UUID) => handleError(defaultApi.getScimSettings({ tenant_uuid: tenant_uuid })),
        setScim: (tenant_uuid: UUID, request: SetScimSettingsRequest) =>
            handleError(defaultApi.setScimSettings({ tenant_uuid: tenant_uuid, SetScimSettingsRequest: request })),
        getEphemeral: () => handleError(defaultApi.getEphemeralDeviceSettings()),
        setEphemeral: (request: SetEphemeralDeviceSettingsRequest) =>
            handleError(
                defaultApi.setEphemeralDeviceSettings({
                    SetEphemeralDeviceSettingsRequest: request,
                }),
            ),
    },
    ldap: {
        testSettings: (tenant_uuid: UUID, request: SetLdapQuerySettingsRequest) =>
            handleError(defaultApi.testLdapQuerySettings({ tenant_uuid, SetLdapQuerySettingsRequest: request })),
        setQuerySettings: (tenant_uuid: UUID, request: SetLdapQuerySettingsRequest) =>
            handleError(defaultApi.setLdapQuerySettings({ tenant_uuid, SetLdapQuerySettingsRequest: request })),
        setConnectionSettings: (tenant_uuid: UUID, request: SetLdapConnectionSettingsRequest) =>
            handleError(
                defaultApi.setLdapConnectionSettings({ tenant_uuid, SetLdapConnectionSettingsRequest: request }),
            ),
        getSettings: (tenant_uuid: UUID) => handleError(defaultApi.getLdapSettings({ tenant_uuid })),
    },
    users: {
        getAll: () => handleError(defaultApi.getAllUsers()),
        expireDevices: (uuid: UUID) => handleError(defaultApi.expireUserDevices({ uuid })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleUser({ uuid: uuid })),
        invite: (request: InviteUserRequest) => handleError(defaultApi.inviteUser({ InviteUserRequest: request })),
        expireInvitation: (user_uuid: UUID) => handleError(defaultApi.expireInvitation({ user_uuid })),
        recreateInvitation: (user_uuid: UUID, request: RecreateInvitationRequest) =>
            handleError(defaultApi.recreateInvitation({ user_uuid, RecreateInvitationRequest: request })),
        renewAccess: (user_uuid: UUID, request: RenewUserAccessRequest) =>
            handleError(defaultApi.renewUserAccess({ user_uuid, RenewUserAccessRequest: request })),
        revokeAccess: (user_uuid: UUID) => handleError(defaultApi.revokeUserAccess({ user_uuid })),
        deleteLocal: (user_uuid: UUID) => handleError(defaultApi.deleteLocalOrInvitedUser({ user_uuid })),
    },
    groups: {
        getAll: (request: GetAllGroupsRequest) =>
            handleError(defaultApi.getAllGroups({ GetAllGroupsRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleGroup({ uuid })),
        getMembers: (uuid: UUID, request: GetSingleGroupMembersRequest) =>
            handleError(
                defaultApi.getSingleGroupMembers({
                    uuid,
                    GetSingleGroupMembersRequest: request,
                }),
            ),
        update: (uuid: UUID, request: UpdateGroupRequest) =>
            handleError(
                defaultApi.updateGroup({
                    uuid,
                    UpdateGroupRequest: request,
                }),
            ),
        local: {
            create: (request: CreateLocalGroupRequest) =>
                handleError(defaultApi.createLocalGroup({ CreateLocalGroupRequest: request })),
            delete: (uuid: UUID) => handleError(defaultApi.deleteLocalGroup({ uuid })),
            addMember: (group: UUID, user: UUID) =>
                handleError(
                    defaultApi.addLocalGroupMember({
                        uuid: group,
                        SingleUuid: { uuid: user },
                    }),
                ),
            removeMember: (group: UUID, user: UUID) =>
                handleError(
                    defaultApi.removeLocalGroupMember({
                        uuid: group,
                        SingleUuid: { uuid: user },
                    }),
                ),
            getUsersNotInGroup: (group: UUID, request: GetUserNotInGroupRequest) =>
                handleError(defaultApi.getUsersNotInGroup({ uuid: group, GetUserNotInGroupRequest: request })),
        },
    },
    deviceGroups: {
        getAll: (request: GetAllDeviceGroupsRequest) =>
            handleError(defaultApi.getAllDeviceGroups({ GetAllDeviceGroupsRequest: request })),
        getSingle: (device_group_uuid: UUID) => handleError(defaultApi.getDeviceGroup({ device_group_uuid })),
        create: (request: CreateDeviceGroupRequest) =>
            handleError(defaultApi.createDeviceGroup({ CreateDeviceGroupRequest: request })),
        setName: (device_group_uuid: UUID, name: string) =>
            handleError(
                defaultApi.setDeviceGroupName({
                    device_group_uuid,
                    SetDeviceGroupNameRequest: { name },
                }),
            ),
        setDescription: (device_group_uuid: UUID, description: string) =>
            handleError(
                defaultApi.setDeviceGroupDescription({
                    device_group_uuid,
                    SetDeviceGroupDescriptionRequest: { description },
                }),
            ),
        delete: (device_group_uuid: UUID) => handleError(defaultApi.deleteDeviceGroup({ device_group_uuid })),
    },
    tags: {
        getAll: (request: GetAllTagsRequest) => handleError(defaultApi.getAllTags({ GetAllTagsRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getTag({ uuid })),
        create: (request: CreateTagRequest) => handleError(defaultApi.createTag({ CreateTagRequest: request })),
        setName: (tag_uuid: UUID, name: string) =>
            handleError(
                defaultApi.setTagName({
                    tag_uuid,
                    SetTagNameRequest: { name },
                }),
            ),
        setDescription: (tag_uuid: UUID, description: string) =>
            handleError(
                defaultApi.setTagDescription({
                    tag_uuid,
                    SetTagDescriptionRequest: { description },
                }),
            ),
        delete: (uuid: UUID) => handleError(defaultApi.deleteTag({ uuid })),
        getAllDirectories: () => handleError(defaultApi.getAllDirectories()),
        createDirectory: (request: CreateTagDirectoryRequest) =>
            handleError(defaultApi.createDirectory({ CreateTagDirectoryRequest: request })),
        updateDirectory: (uuid: UUID, request: UpdateTagDirectoryRequest) =>
            handleError(defaultApi.updateDirectory({ directory_uuid: uuid, UpdateTagDirectoryRequest: request })),
        deleteDirectory: (uuid: UUID) => handleError(defaultApi.deleteDirectory({ directory_uuid: uuid })),
        moveTagToDirectory: (request: MoveTagToAnotherDirectoryRequest) =>
            handleError(defaultApi.moveTagToOtherDirectory({ MoveTagToAnotherDirectoryRequest: request })),
    },
    subnets: {
        getAll: (request: GetAllSubnetsRequest) =>
            handleError(defaultApi.getAllSubnets({ GetAllSubnetsRequest: request })),
        getSingle: (subnet_uuid: UUID) => handleError(defaultApi.getSingleSubnet({ subnet_uuid })),
        enable: (subnet_uuid: UUID, request: EnableSubnetRequest) =>
            handleError(
                defaultApi.enableSubnet({
                    subnet_uuid,
                    EnableSubnetRequest: request,
                }),
            ),
        disable: (subnet_uuid: UUID) => handleError(defaultApi.disableSubnet({ subnet_uuid })),
        delete: (subnet_uuid: UUID) => handleError(defaultApi.deleteSubnet({ subnet_uuid })),
        getPendingRoutes: (request: GetPendingRoutesRequest) =>
            handleError(defaultApi.getPendingSubnetRoutes({ GetPendingRoutesRequest: request })),
        createSubnetWithFstRouter: (subnet_name: string, device_uuid: UUID, description: string) =>
            handleError(
                defaultApi.createSubnetWithFstRouter({
                    CreateSubnetWithFstRouterRequest: { name: subnet_name, router: device_uuid, description },
                }),
            ),
        addRouter: (subnet_uuid: UUID, device_uuid: UUID) =>
            handleError(defaultApi.addSubnetRouter({ subnet_uuid, device_uuid })),
        rejectPendingRoute: (subnet_name: string, device_uuid: UUID) =>
            handleError(
                defaultApi.rejectPendingSubnetRouter({
                    RejectPendingSubnetRouterRequest: { name: subnet_name, router: device_uuid },
                }),
            ),
        removeRouter: (subnet_uuid: UUID, device_uuid: UUID) =>
            handleError(defaultApi.removeSubnetRouter({ subnet_uuid, device_uuid })),
        setDescription: (subnet_uuid: UUID, description: string) =>
            handleError(defaultApi.setSubnetDescription({ subnet_uuid, SetSubnetDescriptionRequest: { description } })),
    },
    preAuthKeys: {
        getAll: () => handleError(defaultApi.getAllPreAuthKeys()),
        create: (request: CreatePreAuthKeyRequest) =>
            handleError(defaultApi.createPreAuthKey({ CreatePreAuthKeyRequest: request })),
        expirePreAuthKey: (uuid: UUID) => handleError(defaultApi.expireKey({ uuid })),
    },
    devices: {
        getAll: () => handleError(defaultApi.getAllDevices()),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleDevice({ uuid })),
        getFromIp: (ip: string) => handleError(defaultApi.getDeviceFromIp({ ip })),
        getFromNames: (pattern: string) => handleError(defaultApi.getDevicesFromName({ name: pattern })),
        setDnsname: (uuid: UUID, request: SetDeviceDnsnameRequest) =>
            handleError(
                defaultApi.setDeviceDnsname({
                    uuid,
                    SetDeviceDnsnameRequest: request,
                }),
            ),
        expireDevice: (uuid: UUID) => handleError(defaultApi.expireDevice({ uuid })),
        deleteDevice: (uuid: UUID) => handleError(defaultApi.deleteDevice({ uuid })),
        addDeviceTag: (device: UUID, tag: UUID) =>
            handleError(
                defaultApi.addDeviceTag({
                    uuid: device,
                    SingleUuid: { uuid: tag },
                }),
            ),
        removeDeviceTag: (device: UUID, tag: UUID) =>
            handleError(
                defaultApi.removeDeviceTag({
                    uuid: device,
                    SingleUuid: { uuid: tag },
                }),
            ),
        addToDeviceGroup: (device: UUID, deviceGroup: UUID) =>
            handleError(
                defaultApi.addDeviceToDeviceGroup({
                    device_uuid: device,
                    SingleUuid: { uuid: deviceGroup },
                }),
            ),
        removeFromDeviceGroup: (device: UUID, deviceGroup: UUID) =>
            handleError(
                defaultApi.removeDeviceFromDeviceGroup({
                    device_uuid: device,
                    SingleUuid: { uuid: deviceGroup },
                }),
            ),
        dumpDeviceState: (uuid: UUID) => handleError(defaultApi.dumpDeviceState({ uuid })),
        setDeviceStateDebugOnly: (uuid: UUID, state: MapResponse) =>
            handleError(defaultApi.setDeviceStateDebugOnly({ uuid, MapResponse: state })),
    },
    changelog: {
        get: (request: GetChangelogRequest) => handleError(defaultApi.getChangelog({ GetChangelogRequest: request })),
    },
    accessRules: {
        getAll: () => handleError(defaultApi.getAllAccessRules()),
        getAllForOptions: () => handleError(defaultApi.getAllAccessRuleOptions()),
        create: (request: CreateAccessRuleRequest) =>
            handleError(defaultApi.createAccessRule({ CreateAccessRuleRequest: request })),
        update: (uuid: UUID, request: UpdateAccessRuleRequest) =>
            handleError(
                defaultApi.updateAccessRule({
                    uuid,
                    UpdateAccessRuleRequest: request,
                }),
            ),
        delete: (uuid: UUID) => handleError(defaultApi.deleteAccessRule({ uuid })),
    },
    rulesets: {
        getPage: (request: GetRulesetsPageRequest) =>
            handleError(defaultApi.getRulesetsPage({ GetRulesetsPageRequest: request })),
        create: (request: CreateRulesetRequest) =>
            handleError(defaultApi.createRuleset({ CreateRulesetRequest: request })),
        get: (ruleset_uuid: UUID) => handleError(defaultApi.getRuleset({ ruleset_uuid })),
        update: (ruleset_uuid: UUID, request: UpdateRulesetRequest) =>
            handleError(defaultApi.updateRuleset({ ruleset_uuid, UpdateRulesetRequest: request })),
        delete: (ruleset_uuid: UUID) => handleError(defaultApi.deleteRuleset({ ruleset_uuid })),
        addRule: (ruleset_uuid: UUID, request: AddRuleToRulesetRequest) =>
            handleError(defaultApi.addRuleToRuleset({ ruleset_uuid, AddRuleToRulesetRequest: request })),
        updateRule: (rule_uuid: UUID, request: UpdateRuleInRulesetRequest) =>
            handleError(defaultApi.updateRuleInRuleset({ rule_uuid, UpdateRuleInRulesetRequest: request })),
        deleteRule: (rule_uuid: UUID) => handleError(defaultApi.deleteRuleFromRuleset({ rule_uuid })),
    },
    rulesetInstances: {
        create: (request: CreateRulesetInstanceRequest) =>
            handleError(defaultApi.createRulesetInstance({ CreateRulesetInstanceRequest: request })),
        get: (instance_uuid: UUID) => handleError(defaultApi.getRulesetInstance({ instance_uuid })),
        delete: (instance_uuid: UUID) => handleError(defaultApi.deleteRulesetInstance({ instance_uuid })),
    },
    dns: {
        get: () => handleError(defaultApi.getGlobalDns()),
        update: (request: FullGlobalDns) => handleError(defaultApi.updateGlobalDns({ FullGlobalDns: request })),
        getAllSplitDns: () => handleError(defaultApi.getSplitDns()),
        getAllExtraDns: (request: GetAllExtraDnsRequest) => handleError(defaultApi.getAllExtraDns(request)),
        getDeviceExtraDns: (uuid: UUID) => handleError(defaultApi.getDeviceExtraDns({ uuid })),
        deleteExtraDns: (uuid: UUID) => handleError(defaultApi.deleteExtraDnsDomain({ uuid })),
        deleteExtraDnsRecord: (uuid: UUID) => handleError(defaultApi.deleteExtraDnsRecord({ uuid })),
        createExtraDnsRecords: (request: CreateExtraDnsRecordsRequest) =>
            handleError(defaultApi.createExtraDnsRecords({ CreateExtraDnsRecordsRequest: request })),
        addSingleExtraDnsRecord: (uuid: UUID, request: AddExtraDnsRecordRequest) =>
            handleError(
                defaultApi.addRecordToExtraDns({
                    uuid,
                    AddExtraDnsRecordRequest: request,
                }),
            ),
        updateExtraDnsDomain: (uuid: UUID, request: UpdateExtraDnsDomainRequest) =>
            handleError(
                defaultApi.updateExtraDnsDomain({
                    uuid,
                    UpdateExtraDnsDomainRequest: request,
                }),
            ),
        updateExtraDnsRecord: (uuid: UUID, request: UpdateExtraDnsRecordRequest) =>
            handleError(
                defaultApi.updateExtraDnsRecord({
                    uuid,
                    UpdateExtraDnsRecordRequest: request,
                }),
            ),
        getPossibleExtraDnsByDevice: (device_uuid: UUID) =>
            handleError(defaultApi.getPossibleExtraDnsForDevice({ uuid: device_uuid })),
        getSearchDomains: () => handleError(defaultApi.getDnsSearchDomains()),
        insertSearchDomain: (request: InsertDnsSearchDomainRequest) =>
            handleError(defaultApi.insertDnsSearchDomain({ InsertDnsSearchDomainRequest: request })),
        moveSearchDomain: (request: MoveDnsSearchDomainRequest) =>
            handleError(defaultApi.moveDnsSearchDomain({ MoveDnsSearchDomainRequest: request })),
        removeSearchDomain: (domain_index: number) => handleError(defaultApi.removeDnsSearchDomain({ domain_index })),
        addDomainSplitDns: (request: CreateSplitDnsDomainRequest) =>
            handleError(defaultApi.addDomainToSplitDns({ CreateSplitDnsDomainRequest: request })),
        updateDomainSplitDns: (uuid: UUID, request: UpdateSplitDnsDomainRequest) =>
            handleError(defaultApi.updateDomainInSplitDns({ domain_uuid: uuid, UpdateSplitDnsDomainRequest: request })),
        deleteDomainSplitDns: (uuid: UUID) => handleError(defaultApi.deleteDomainFromSplitDns({ domain_uuid: uuid })),
        addResolverSplitDns: (uuid: UUID, request: CreateSplitDnsResolverRequest) =>
            handleError(
                defaultApi.addResolverToDomainInSplitDns({ domain_uuid: uuid, CreateSplitDnsResolverRequest: request }),
            ),
        updateResolverSplitDns: (uuid: UUID, request: UpdateSplitDnsResolverRequest) =>
            handleError(
                defaultApi.updateResolverInDomainInSplitDns({
                    resolver_uuid: uuid,
                    UpdateSplitDnsResolverRequest: request,
                }),
            ),
        deleteResolverSplitDns: (uuid: UUID) =>
            handleError(defaultApi.deleteResolverFromDomainInSplitDns({ resolver_uuid: uuid })),
    },
    exitNode: {
        getAll: (request: GetAllExitNodesRequest) =>
            handleError(defaultApi.getAllApprovedExitNodes({ GetAllExitNodesRequest: request })),
        getPending: (request: GetPendingExitNodesRequest) =>
            handleError(defaultApi.getPendingExitNodes({ GetPendingExitNodesRequest: request })),
        delete: (uuid: UUID) => handleError(defaultApi.deleteExitNode({ uuid })),
        reject: (uuid: UUID) => handleError(defaultApi.rejectPendingExitNode({ uuid })),
        approve: (uuid: UUID) => handleError(defaultApi.approvePendingExitNode({ uuid })),
        enable: (uuid: UUID) => handleError(defaultApi.enableExitNode({ uuid })),
        disable: (uuid: UUID) => handleError(defaultApi.disableExitNode({ uuid })),
    },
    plugins: {
        getAll: (request: GetAllPluginsRequest) =>
            handleError(defaultApi.getAllPlugins({ GetAllPluginsRequest: request })),
        getSingle: (plugin_uuid: UUID) => handleError(defaultApi.getSinglePlugin({ plugin_uuid })),
        getVersion: (plugin_uuid: UUID, version: string) =>
            handleError(defaultApi.getPluginVersion({ plugin_uuid, version })),
        install: (plugin_uuid: UUID) => handleError(defaultApi.installPlugin({ plugin_uuid })),
        update: (plugin_uuid: UUID) => handleError(defaultApi.updatePlugin({ plugin_uuid })),
        delete: (plugin_uuid: UUID) => handleError(defaultApi.deletePlugin({ plugin_uuid })),
    },
    oidcProvider: {
        getAll: () => handleError(defaultApi.getAllOidcProvider()),
        getSingle: (provider_uuid: UUID) => handleError(defaultApi.getSingleOidcProvider({ provider_uuid })),
        create: (request: CreateOidcProviderRequest) =>
            handleError(defaultApi.createOidcProvider({ CreateOidcProviderRequest: request })),
        update: (provider_uuid: UUID, request: UpdateOidcProviderRequest) =>
            handleError(defaultApi.updateOidcProvider({ provider_uuid, UpdateOidcProviderRequest: request })),
        delete: (provider_uuid: UUID) => handleError(defaultApi.deleteOidcProvider({ provider_uuid })),
    },
    tenants: {
        getAll: () => handleError(defaultApi.getAllTenants()),
        getSingle: (tenant_uuid: UUID) => handleError(defaultApi.getSingleTenant({ tenant_uuid })),
        create: (request: CreateTenantRequest) =>
            handleError(defaultApi.createTenant({ CreateTenantRequest: request })),
        update: (tenant_uuid: UUID, request: UpdateTenantRequest) =>
            handleError(defaultApi.updateTenant({ tenant_uuid, UpdateTenantRequest: request })),
        getDeletePreview: (tenant_uuid: UUID) => handleError(defaultApi.getDeletePreview({ tenant_uuid })),
        delete: (tenant_uuid: UUID) => handleError(defaultApi.deleteTenant({ tenant_uuid })),
    },
    tenantsDns: {
        getAll: (tenant_uuid: UUID) => handleError(defaultApi.getTenantDomains({ tenant_uuid })),
        addDomain: (tenant_uuid: UUID, domain_uuid: UUID) =>
            handleError(defaultApi.addTenantDomain({ tenant_uuid, AddTenantDomainRequest: { uuid: domain_uuid } })),
        removeDomain: (tenant_uuid: UUID, domain_uuid: UUID) =>
            handleError(
                defaultApi.removeTenantDomain({ tenant_uuid, RemoveTenantDomainRequest: { uuid: domain_uuid } }),
            ),
        getSearchDomain: (tenant_uuid: UUID) => handleError(defaultApi.getTenantSearchDomains({ tenant_uuid })),
        insertSearchDomain: (tenant_uuid: UUID, request: InsertTenantSearchDomainRequest) =>
            handleError(
                defaultApi.insertTenantDnsSearchDomain({ tenant_uuid, InsertTenantSearchDomainRequest: request }),
            ),
        moveSearchDomain: (tenant_uuid: UUID, request: MoveDnsSearchDomainRequest) =>
            handleError(defaultApi.moveTenantDnsSearchDomain({ tenant_uuid, MoveTenantSearchDomainRequest: request })),
        removeSearchDomain: (tenant_uuid: UUID, domain_index: number) =>
            handleError(defaultApi.removeTenantDnsSearchDomain({ tenant_uuid, domain_index })),
    },
    accessVisualization: {
        getGraph: () => handleError(defaultApi.getAccessGraph()),
        checkAccess: (request: CheckDeviceAccessRequest) =>
            handleError(defaultApi.checkDeviceAccess({ CheckDeviceAccessRequest: request })),
        getProblems: () => handleError(defaultApi.getAccessProblems()),
    },
    /*
    data_transfer: {
        export: () => handleError(defaultApi.getExport()),

        initImport: (request: PrepareDataImportRequest) =>
            handleError(defaultApi.prepareDataImport({ body: request.body })),
        getImport: () => handleError(defaultApi.getImportState()),
        updateImport: (request: UpdateImportStateRequest) =>
            handleError(defaultApi.updateImportState({ UpdateImportStateRequest: request })),
        performImport: () => handleError(defaultApi.importDataFromSession()),
    },
    */
};

/**
 * Wraps a promise returned by the generated SDK which handles its errors and returns a {@link Result}
 *
 * @param promise The promise to wrap. This should be a promise defined in the generated part of the API
 *
 * @returns a new promise with a result that wraps errors from the API
 */
export async function handleError<T>(promise: Promise<T>): Promise<T> {
    try {
        return await promise;
    } catch (e) {
        let msg;
        if (e instanceof ResponseError) {
            if (e.response.status === 401) {
                // HTTP/2 has no reason phrase, so e.response.statusText is "" there. Report a
                // stable sentinel (keyed off the 401 status code) so ErrorContext's
                // `error === "Unauthorized"` check works over HTTP/2, not just HTTP/1.1.
                msg = "Unauthorized";
            } else {
                try {
                    const err = await e.response.json();
                    msg = `${e.response.statusText}. TraceId: ${err.trace_id}`;
                } catch {
                    Console.error("Got invalid json", e.response.body);
                    msg = `${e.response.statusText}. The server's response was invalid json.`;
                }
            }
        } else if (e instanceof RequiredError) {
            Console.error(e);
            msg = "The server's response didn't match the spec";
        } else {
            Console.error("Unknown error occurred:", e);
            msg = "Unknown error occurred";
        }
        ERROR_STORE.report(msg);
        throw msg;
    }
}
